import React from 'react'
import styled from 'styled-components'
import Filter from './Filter'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { selectDashboardMode, selectLoading, selectOpen, setOpen } from '../ducks/navigationSlice'
import _ from 'lodash'
import Utils from 'live/lib/utils'
import { LoadingComponent } from './LoadingComponent'
const TreeComponent = React.lazy(() => import('./TreeComponent'))

interface SideBarProps {
    baseNodes: DashboardNode[]
    nodeToDashboard: SimplifiedNavigation[]
    dropdownFilters: { [k: string]: { value: string; label: string }[] }
    forceExpanded?: boolean
}

const SideBar: React.FC<SideBarProps> = ({ baseNodes, nodeToDashboard, dropdownFilters, forceExpanded }) => {
    const dispatch = useAppDispatch()
    const dashboardMode = useAppSelector(state => selectDashboardMode(state))
    const loading = useAppSelector(state => selectLoading(state))
    const open = useAppSelector(state => selectOpen(state))
    const nodes = React.useMemo(() => {
        return Array.isArray(baseNodes) ? _.cloneDeep(baseNodes) : []
    }, [baseNodes])
    const collapsableDivRef = React.useRef<HTMLDivElement>(null)

    const [width, setWidth] = React.useState(0)

    React.useEffect(() => {
        const onResize = (dom_elem: Element, callback: { (): void; (): void }) => {
            const resizeObserver = new ResizeObserver(() => callback())
            resizeObserver.observe(dom_elem)
        }

        // @ts-ignore
        onResize(collapsableDivRef?.current, function () {
            // @ts-ignore
            setWidth(collapsableDivRef?.current?.clientWidth)
        })
    }, [])

    const handleSetOpen = (value: boolean) => {
        dispatch(setOpen(value))
        localStorage.setItem(`user/${window.Live.currentUser.id}/adnocSidebarState`, value.toString())
    }

    return (
        <>
            {/* {
                open &&
                <BlackoutDiv expanded={open}/>
            } */}
            {/* <ClickAwayListener onClickAway={() => handleSetOpen(false)}> */}
            <React.Suspense fallback={<LoadingComponent width={width} />}>
                <CollapsableDiv
                    mode={dashboardMode}
                    customWidth={315}
                    expanded={forceExpanded ? true : open}
                    ref={collapsableDivRef}
                    isMobile={Utils.isMobile()}
                >
                    {!forceExpanded && (
                        <Navigation
                            left={width}
                            expanded={forceExpanded ? true : open}
                            onClick={() => handleSetOpen(!open)}
                        >
                            <i className={`fas fa-chevron-${open ? 'left' : 'right'}`}></i>
                            {!open && <span className="navigationSpan">PROJECTS</span>}
                        </Navigation>
                    )}

                    <div style={{ height: 36, paddingLeft: 20, paddingTop: 10, overflow: 'hidden' }}>
                        <span className="navigationSpanNormal">PROJECTS</span>
                    </div>

                    <Filter
                        sideBarExpanded={forceExpanded ? true : open}
                        sidebarWidth={width}
                        dropdownFilters={dropdownFilters}
                    />

                    <TreeContainer>
                        {loading && <LoadingComponent width={width} />}

                        <React.Suspense fallback={<LoadingComponent width={width} />}>
                            <TreeComponent nodeToDashboard={nodeToDashboard} nodes={nodes} />
                        </React.Suspense>
                    </TreeContainer>
                </CollapsableDiv>
            </React.Suspense>
            {/* </ClickAwayListener> */}
        </>
    )
}

export default SideBar

const CollapsableDiv = styled.div<{ expanded: boolean; customWidth: number; isMobile: boolean; mode: string }>`
    position: ${props => (props.isMobile ? 'absolute' : 'unset')};
    /* border-right: 1px solid var(--gray-300); */
    top: 0;
    left: -0.5px;
    height: 100%;
    width: ${(props: { expanded: any; customWidth: any; isMobile: boolean }) =>
        props.expanded ? (props.isMobile ? '100%' : `${props.customWidth}px`) : '0px'};
    background: var(--gray-200);
    z-index: ${props => (props.isMobile ? 999 : 9998)};
    transition: 0.5s;
    display: ${props => (props.mode === 'fullscreen' ? 'none' : 'flex')};
    flex-direction: column;

    .navigationSpanNormal {
        font-family: SourceSansPro-Semibold;
        color: var(--blue-700);
        font-size: 14px;
    }
`

const Navigation = styled.div<{ expanded: boolean; left: number }>`
    position: absolute;
    display: flex;
    flex-direction: column;
    /* justify-content: center; */
    width: 32px;
    height: ${(props: { expanded: any }) => (props.expanded ? '36px' : '100%')};
    background-color: var(--gray-200);
    align-items: center;
    left: ${(props: { expanded: any; left: number }) => (props.expanded ? props.left - 35 : 0)}px;
    color: var(--blue-700);
    cursor: pointer;
    /* border: 1px solid var(--gray-200); */
    gap: 15px;
    transition: ${(props: { expanded: any }) => (props.expanded ? 0 : 0.5)}s;

    i {
        font-size: 15px;
        margin-top: 12px;
    }

    .navigationSpan {
        font-family: SourceSansPro-Semibold;
        font-size: 14px;
        writing-mode: vertical-rl;
        transform: rotate(180deg);
    }
`

const BlackoutDiv = styled.div<{ expanded: boolean }>`
    background: rgba(102, 102, 102, 0.3);
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 100;
    display: ${(props: { expanded: any }) => (props.expanded ? 'unset' : 'none')};
`

const TreeContainer = styled.div`
    height: 100%;
    width: 100%;
    margin: 13px 0px 0px 0px;
    overflow: auto;
    border-top: 1px solid var(--gray-300);

    .treeHidden {
        display: none !important;
    }

    ul > li {
        padding-bottom: 10px !important;
    }

    .treeLabel {
        color: var(--gray-800);
        font-size: 14px !important;
    }

    .treeIcon {
        svg {
            font-size: 24px !important;
            color: var(--gray-700);
        }

        .selectedTreeItem {
            border: none;
            background-color: var(--gray-300) !important;
        }
    }

    .selectedTreeItem {
        border: 1px solid var(--blue-500);
        background-color: var(--blue-300) !important;
    }

    .treeItemRoot {
        padding: 3px 0px;
    }

    .treeItemContent {
        :hover {
            background-color: var(--blue-200) !important;
        }
    }

    .treeItemFocused {
        background-color: transparent !important;
    }

    flex: 1;

    &::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 6px rgba(var(--gray-1000), 0.3);
        background-color: var(--gray-200);
    }

    flex: 1;

    &::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 6px rgba(var(--gray-1000), 0.3);
        background-color: var(--gray-200);
    }

    &::-webkit-scrollbar {
        width: 4px;
        background-color: var(--gray-200);
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--gray-600);
    }

    scrollbar-color: var(--gray-600);
    scrollbar-width: thin;
`
